import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { CloseOutlined, FileUnknownOutlined, UploadOutlined } from "@ant-design/icons";
import { Col, Input, InputNumber, Row, Select, notification } from "antd";
import { ContentWrapper } from "components";
import Dropzone from "react-dropzone";
import type { HelpFile } from "screens/Admin/HelpFiles/types";
import { store } from "store";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import { LanguageSchema } from "utils/types";
import { translate } from "utils/utils";
import Strings from "utils/strings";

type State = {
	isNew: boolean;
	file?: HelpFile;
	hasUnsavedFields: boolean;
	language: keyof LanguageSchema;
	uploadFile?: {
		[key in keyof LanguageSchema]?: File;
	};
} & HelpFile;
type Props = {
	location: any;
	dispatch: typeof store.dispatch;
	match: any;
};

class FileDetail extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			title: { pt: "", en: "" },
			description: { pt: "", en: "" },
			url: { pt: "", en: "" },
			successLevel: 1,
			fileType: "video",
			isActive: true,
			topic: props.location.state?.topic || "other",

			isNew: props.match.params.id === "new",
			hasUnsavedFields: false,
			language: "pt"
		};
	}

	componentDidMount(): void {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		this.breadcrumb();
		this.getData();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData() {
		const { isNew } = this.state;
		const { dispatch, match } = this.props;

		if (isNew) return;

		dispatch(setLoader(true));

		try {
			const response = await API.get({
				url: Endpoints.uriFiles(match.params.id)
			});

			if (response.ok) {
				const { file = {} } = response.data.results;
				this.setState({ ...file, file });
			} else {
				notification.error({
					message: Strings.settings.files,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});

				dispatch(push("/settings/files"));
			}
		} catch (err: unknown) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
			dispatch(push("/settings/files"));
		}

		dispatch(setLoader(false));
	}

	async saveFile() {
		const { isNew, title, description, url, successLevel, fileType, uploadFile, topic = "other", isActive } = this.state;
		const { dispatch, match } = this.props;

		if (!this.validFile()) return;

		dispatch(setLoader(true));

		try {
			let newUrl: LanguageSchema = url;

			Object.entries(newUrl).forEach(([key, value]) => {
				if (value.includes("/watch?v=")) {
					newUrl[key as keyof LanguageSchema] = value.replace("/watch?v=", "/embed/");
				}
			});

			const request = isNew ? API.post : API.put;
			const body = new FormData();

			body.append("title", JSON.stringify(title));
			body.append("description", JSON.stringify(description));
			body.append("fileType", fileType);
			body.append("url", JSON.stringify(newUrl));
			body.append("successLevel", String(successLevel));
			body.append("isActive", String(isActive));
			body.append("topic", topic);

			if (uploadFile) {
				Object.entries(uploadFile).forEach(([key, value]) => {
					if (value) {
						body.append(`file_${key}`, value);
					}
				});
			}

			const response = await request({
				url: Endpoints.uriFiles(isNew ? "" : match.params.id),
				data: body
			});

			if (response?.ok) {
				const { file } = response.data.results || {};
				this.setState({ ...file, file, hasUnsavedFields: false });

				if (isNew) {
					dispatch(push("/settings/files"));
				}

				notification.success({
					message: Strings.settings.files,
					description: response?.data?.message,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.settings.files,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err: unknown) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	validFile() {
		const { title, url, fileType, successLevel, topic } = this.state;

		if (!translate(title)) {
			notification.error({
				message: Strings.settings.files,
				description: Strings.settings.titleMandatory,
				placement: "bottomRight",
				duration: 5
			});

			return false;
		}

		if (fileType === "video" && !translate(url)) {
			notification.error({
				message: Strings.settings.files,
				description: Strings.settings.urlMandatory,
				placement: "bottomRight",
				duration: 5
			});

			return false;
		}

		if (fileType === "video" && translate(url)) {
			const youtubeRegex = new RegExp(/^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/);

			const invalid = Object.values(url).some((value) => {
				if (!value) return false;
				if (!youtubeRegex.test(value)) {
					notification.error({
						message: Strings.settings.files,
						description: Strings.settings.invalidUrl,
						placement: "bottomRight",
						duration: 5
					});

					return true;
				}

				return false;
			});

			if (invalid) return false;
		}

		if (!successLevel && (topic !== "panflet" && topic !== "productFile")) {
			notification.error({
				message: Strings.settings.files,
				description: Strings.settings.successLevelMandatory,
				placement: "bottomRight",
				duration: 5
			});

			return false;
		}

		if (successLevel <= 0) {
			notification.error({
				message: Strings.settings.files,
				description: Strings.settings.invalidSuccessLevel,
				placement: "bottomRight",
				duration: 5
			});

			return false;
		}

		return true;
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { title, isNew, _id } = this.state;

				return {
					locations: [
						{
							text: Strings.sidebar.settings,
							route: "/settings",
							icon: "preferences"
						},
						{
							text: Strings.settings.files,
							route: "/settings/files",
							icon: <FileUnknownOutlined translate={null} />,
							iconType: "antd"
						},
						{
							text: isNew ? Strings.settings.newFile : translate(title) || Strings.generic.loading,
							icon: _id ? "pencil-outline" : "plus"
						}
					],
					actions: [
						{
							type: "switch",
							text: Strings.generic.active,
							value: this.state.isActive,
							small: {
								switch: true,
								text: true
							},
							separator: "right",
							onClick: (value: boolean) => this.setState({ isActive: value, hasUnsavedFields: true })
						},
						{
							type: "language",
							value: this.state.language,
							showArrow: true,
							showSearch: false,
							separator: "right",
							onChange: (value: keyof LanguageSchema) => this.setState({ language: value })
						},
						{
							type: "button",
							text: Strings.generic.save,
							disabled: !this.state.hasUnsavedFields,
							className: "BreadcrumbSaveButton",
							isSave: true,
							hasIcon: true,
							onClick: () => this.saveFile()
						}
					]
				};
			})
		);
	}

	onDrop(files: any) {
		const { language } = this.state;

		try {
			const file = files[files.length - 1];

			this.setState((prevState: State) => ({
				uploadFile: {
					...prevState.uploadFile,
					[language]: file
				},
				hasUnsavedFields: true
			}));
		} catch (err) {
			notification.warn({
				message: Strings.errors.unsupportedFile,
				description: Strings.errors.fileNotSupported,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	render() {
		const { title, description, url, successLevel, fileType, language, uploadFile, topic } = this.state;

		const linkStartsWithHttp = url?.[language]?.startsWith("http");

		console.log('topic', topic);

		return (
			<div className="ScreenHelpFile">
				<Helmet>
					<title>{Strings.settings.files}</title>
					<meta
						name="description"
						content="Setup the files and its levels to be shown for your partners according to their level."
					/>
				</Helmet>
				<ContentWrapper>
					<Row gutter={[20, 10]}>
						<Col xs={24} md={12}>
							<Row gutter={[20, 10]}>
								<Col xs={24}>
									<label htmlFor="file_title" className="InputLabel --label-required">
										{Strings.pages.title}
									</label>
									<Input
										id="file_title"
										style={{ height: 40 }}
										value={title?.[language] || ""}
										placeholder={Strings.pages.title}
										onChange={(event: any) => {
											const value = event.target.value;

											this.setState((prevState: State) => ({
												title: {
													...prevState.title,
													[language]: value
												},
												hasUnsavedFields: true
											}));
										}}
									/>
								</Col>
								<Col xs={24} xl={6}>
									<label htmlFor="file_type" className="InputLabel --label-required">
										{Strings.settings.fileType}
									</label>
									<Select
										style={{ width: "100%" }}
										value={fileType}
										placeholder={Strings.settings.fileType}
										onChange={(value: "video" | "pdf") => {
											this.setState({ fileType: value, hasUnsavedFields: true });
										}}
										showSearch
										showArrow
										filterOption={(input: any, option: any) =>
											option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
										}
									>
										<Select.Option value="pdf">PDF</Select.Option>
										{!(topic === "panflet" || topic === "productFile") && (
											<Select.Option value="video">{Strings.settings.youtubeVideo}</Select.Option>
										)}
									</Select>
								</Col>
								{fileType === "video" || (fileType === "pdf" && linkStartsWithHttp) ? (
									<Col xs={24} xl={18}>
										<label htmlFor="file_url" className="InputLabel --label-required">
											{Strings.settings.url}
										</label>
										<Input
											id="file_url"
											style={{ height: 40 }}
											readOnly={fileType === "pdf"}
											value={url?.[language] || ""}
											placeholder={Strings.settings.url}
											suffix={
												fileType === "pdf" && url[language]?.includes("/files/help_") ? (
													<span
														onClick={(e: React.MouseEvent) => {
															e.preventDefault();
															e.stopPropagation();
															this.setState((prevState: State) => ({
																url: { ...prevState.url, [language]: "" }
															}));
														}}
													>
														<CloseOutlined translate={null} />
													</span>
												) : null
											}
											onChange={(event: any) => {
												const value = event.target.value;

												this.setState((prevState: State) => ({
													url: {
														...prevState.url,
														[language]: value
													},
													hasUnsavedFields: true
												}));
											}}
										/>
									</Col>
								) : (
									<Col xs={24} xl={18}>
										<Dropzone
											accept="application/pdf"
											className="FilePdfDropzone"
											onDrop={(files: File[]) => this.onDrop(files)}
										>
											<label htmlFor="file" className="InputLabel --label-required">
												{Strings.settings.file}
											</label>
											<Input
												id="file"
												style={{ height: 40, cursor: "pointer" }}
												value={uploadFile?.[language]?.name || ""}
												readOnly
												placeholder={Strings.settings.selectPdf}
												suffix={
													uploadFile?.[language] ? (
														<span
															onClick={(e: React.MouseEvent) => {
																e.preventDefault();
																e.stopPropagation();
																this.setState((prevState: State) => ({
																	uploadFile: { ...prevState.uploadFile, [language]: null }
																}));
															}}
														>
															<CloseOutlined translate={null} />
														</span>
													) : (
														<UploadOutlined translate={null} />
													)
												}
											/>
										</Dropzone>
									</Col>
								)}
								{!(topic === "panflet" || topic === "productFile") && (
									<Col xs={24} md={12}>
										<label htmlFor="partner_level" className="InputLabel --label-required">
											{Strings.settings.successLevel}
										</label>
										<InputNumber
											id="partner_level"
											value={successLevel}
											style={{ width: "100%" }}
											min={1}
											placeholder={Strings.settings.successLevel}
											onChange={(value: number) => {
												this.setState({
													successLevel: value,
													hasUnsavedFields: true
												});
											}}
										/>
									</Col>
								)}
								<Col xs={24} md={12}>
									<label htmlFor="file_topic" className="InputLabel">
										{Strings.settings.topic}
									</label>
									<Select
										id="file_topic"
										style={{ width: "100%" }}
										value={topic}
										placeholder={Strings.settings.topic}
										onChange={(value: string) => {
											if (value === "panflet" || value === "productFile") {
												this.setState({ topic: value, fileType: "pdf", hasUnsavedFields: true });
											} else {
												this.setState({ topic: value, hasUnsavedFields: true });
											}
										}}
										showSearch
										showArrow
										filterOption={(input: any, option: any) =>
											option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
										}
									>
										<Select.Option value="product">{Strings.settings.topicProduct}</Select.Option>
										<Select.Option value="business">{Strings.settings.topicBusiness}</Select.Option>
										<Select.Option value="panflet">{Strings.settings.panflet}</Select.Option>
										<Select.Option value="productFile">{Strings.settings.productFile}</Select.Option>
										<Select.Option value="other">{Strings.settings.other}</Select.Option>
									</Select>
								</Col>
							</Row>
						</Col>
						<Col xs={24} md={12}>
							<label htmlFor="file_description" className="InputLabel">
								{Strings.fields.description}
							</label>
							<Input.TextArea
								id="file_description"
								rows={4}
								style={{ height: "calc(100% - 30px)", resize: "none" }}
								value={description?.[language] || ""}
								placeholder={Strings.fields.description}
								onChange={(event: any) => {
									const value = event.target.value;

									this.setState((prevState: State) => ({
										description: {
											...prevState.description,
											[language]: value
										},
										hasUnsavedFields: true
									}));
								}}
							/>
						</Col>
					</Row>
				</ContentWrapper>
			</div>
		);
	}
}

const mapStateToProps = (state: { language: string }) => ({
	language: state.language
});

export default connect(mapStateToProps)(FileDetail);
